import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../style/style-components/analytics-consent.css";

export const CONSENT_KEY = "wisecursor-analytics-consent";

const appendScript = (id, src) => new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
        resolve(existing);
        return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.id = id;
    script.src = src;
    script.addEventListener("load", () => resolve(script), { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.appendChild(script);
});

export const startAnalytics = () => {
    if (window.__WISECURSOR_ANALYTICS_LOADED__) return;
    window.__WISECURSOR_ANALYTICS_LOADED__ = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", "G-FEQRJS7H7V", { anonymize_ip: true });
    appendScript("wisecursor-google-analytics", "https://www.googletagmanager.com/gtag/js?id=G-FEQRJS7H7V").catch(() => undefined);

    window.hj = window.hj || function hotjar() { (window.hj.q = window.hj.q || []).push(arguments); };
    window._hjSettings = { hjid: 5059184, hjsv: 6 };
    appendScript("wisecursor-hotjar", `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`).catch(() => undefined);

    window.clarity = window.clarity || function clarity() { (window.clarity.q = window.clarity.q || []).push(arguments); };
    appendScript("wisecursor-clarity", "https://www.clarity.ms/tag/n48552qldj").catch(() => undefined);

    window.trq = window.trq || function trq() { (window.trq.q = window.trq.q || []).push(arguments); };
    window.trq("account", "t-66957605c76d02a74c7be67a");
    window.trq("collectorUrl", "https://collector.capturly.com/collect");
    window._paq = window._paq || [];
    window._paq.push(["enableLinkTracking"]);
    window._paq.push(["setTrackerUrl", "https://collector.capturly.com/track"]);
    window._paq.push(["setSiteId", "6869"]);
    appendScript("wisecursor-capturly", "https://capturly.com/capturly-track-js.js").catch(() => undefined);
    appendScript("wisecursor-gloria", "https://app.iamgloria.com/chat-plugin/gloria.js?tenantId=667dd7983da5a136d2de788a").catch(() => undefined);

    Promise.all([
        appendScript("wisecursor-amplitude", "https://cdn.amplitude.com/libs/analytics-browser-2.11.1-min.js.gz"),
        appendScript("wisecursor-amplitude-session", "https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.6.22-min.js.gz"),
    ]).then(() => {
        if (!window.amplitude || !window.sessionReplay) return;
        window.amplitude.add(window.sessionReplay.plugin({ sampleRate: 1 }));
        window.amplitude.init("87f2383a2ca5d26b971232fecbe8e431", {
            autocapture: { elementInteractions: true },
        });
    }).catch(() => {
        window.__WISECURSOR_ANALYTICS_LOADED__ = false;
    });
};

const AnalyticsConsent = () => {
    const [choice, setChoice] = useState(() => localStorage.getItem(CONSENT_KEY));
    const [preferencesOpen, setPreferencesOpen] = useState(() => !localStorage.getItem(CONSENT_KEY));

    useEffect(() => {
        if (choice === "accepted") startAnalytics();
    }, [choice]);

    useEffect(() => {
        const openPreferences = () => setPreferencesOpen(true);
        window.addEventListener("wisecursor:open-cookie-preferences", openPreferences);
        return () => window.removeEventListener("wisecursor:open-cookie-preferences", openPreferences);
    }, []);

    const saveChoice = (nextChoice) => {
        const shouldReload = nextChoice === "declined" && window.__WISECURSOR_ANALYTICS_LOADED__;
        localStorage.setItem(CONSENT_KEY, nextChoice);
        setChoice(nextChoice);
        setPreferencesOpen(false);
        if (shouldReload) window.location.reload();
    };

    if (!preferencesOpen) return null;

    return (
        <section aria-label="Analytics preferences" aria-live="polite" className="analytics-consent">
            <div>
                <h2>Your privacy choices</h2>
                <p>
                    WiseCursor uses optional analytics, session-replay, and chat tools to understand website usage and support visitors. They stay off unless you accept. The contact form uses Deftform and reCAPTCHA to provide the requested form service. <Link to="/privacy">Read the privacy notice.</Link>
                </p>
            </div>
            <div className="analytics-consent__actions">
                <button className="analytics-consent__secondary" onClick={() => saveChoice("declined")} type="button">Continue without analytics</button>
                <button className="analytics-consent__primary" onClick={() => saveChoice("accepted")} type="button">Accept analytics</button>
            </div>
        </section>
    );
};

export default AnalyticsConsent;
